import { FiltersForQuery } from './types';

// Utility to format numbers with commas
export function formatNumberWithCommas(value: string | number) {
  if (value === '' || value === null || value === undefined) return '';
  const num = typeof value === 'string' ? value.replace(/[^0-9]/g, '') : value.toString();
  return num.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

export function filtersToQuery(filters: FiltersForQuery) {
  const params = new URLSearchParams();
  if (filters.city) params.set('city', filters.city);
  if (filters.state) params.set('state', filters.state);
  if (filters.zipCode) params.set('zipCode', filters.zipCode);
  if (filters.maxHoaFees) params.set('maxHoaFees', filters.maxHoaFees);
  if (filters.noHoa) params.set('noHoa', filters.noHoa ? '1' : '0');
  if (filters.priceRange && (filters.priceRange[0] !== null || filters.priceRange[1] !== null)) {
    params.set('priceMin', filters.priceRange[0]?.toString() ?? '');
    params.set('priceMax', filters.priceRange[1]?.toString() ?? '');
  }
  if (typeof filters.beds === 'string' && filters.beds) params.set('beds', filters.beds);
  if (typeof filters.baths === 'string' && filters.baths) params.set('baths', filters.baths);
  if (filters.propertyType) params.set('propertyType', filters.propertyType);
  return params.toString();
}

export function queryToFilters(query: string): FiltersForQuery {
  const params = new URLSearchParams(query);
  const priceMin = params.get('priceMin');
  const priceMax = params.get('priceMax');
  const beds = params.get('beds');
  const baths = params.get('baths');
  return {
    city: params.get('city') || '',
    state: params.get('state') || '',
    zipCode: params.get('zipCode') || '',
    maxHoaFees: params.get('maxHoaFees') || '',
    noHoa: params.get('noHoa') === '1',
    priceRange: [
      priceMin && !isNaN(Number(priceMin)) ? Number(priceMin) : null,
      priceMax && !isNaN(Number(priceMax)) ? Number(priceMax) : null,
    ] as [number | null, number | null],
    beds: typeof beds === 'string' ? beds : null,
    baths: typeof baths === 'string' ? baths : null,
    propertyType: params.get('propertyType') || null,
  };
}

// Removed Google Maps - now using OpenStreetMap

// Import property class cache utilities
import { getPropertyClassLabelsAsStrings, initializePropertyClassCache, convertSelectedPropertyTypesToApiString, getPropertyClassApiValuesAsStrings } from '../../lib/utils/property-class-cache';

// Initialize cache on module load
initializePropertyClassCache();

// Property type constants - now using cached labels
export const rentPropertyTypes = getPropertyClassLabelsAsStrings('rent');

export const buyPropertyTypes = getPropertyClassLabelsAsStrings('buy');

// Also provide sold property types for consistency
export const soldPropertyTypes = getPropertyClassLabelsAsStrings('sold');

// Property type conversion utilities for API calls
export function convertPropertyTypesForApi(selectedPropertyTypes: string[], category: 'buy' | 'rent' | 'sold'): string {
  return convertSelectedPropertyTypesToApiString(selectedPropertyTypes, category);
}

// Helper function to get selected property types for current tab
export function getSelectedPropertyTypesForTab(
  activeFilterTab: 'buy' | 'rent' | 'sold',
  selectedPropertyTypesBuySold: string[],
  selectedPropertyTypesRent: string[]
): string[] {
  return activeFilterTab === 'rent' ? selectedPropertyTypesRent : selectedPropertyTypesBuySold;
}

// Utility functions for lot size conversions
export const sqftToAcres = (sqft: number): number => sqft / 43560;
export const acresToSqft = (acres: number): number => acres * 43560;

// Constants for hybrid lot size slider
const SQFT_MAX = 10890; // 0.25 acres in sqft (43560 * 0.25)
const ACRES_MIN = 0.25;
const ACRES_MAX_MIN = 20; // for min slider
const ACRES_MAX_MAX = 25; // for max slider
const SLIDER_MAX = 1000;
const SLIDER_SPLIT = 500;

export function sliderToLotSize(val: number, isMax = false): number {
  if (val <= SLIDER_SPLIT) {
    // First half: linear scale for sqft (0 to SQFT_MAX)
    return Math.round((val / SLIDER_SPLIT) * SQFT_MAX);
  } else {
    // Second half: exponential scale for acres (ACRES_MIN to ACRES_MAX)
    const maxAcres = isMax ? ACRES_MAX_MAX : ACRES_MAX_MIN;
    const normalizedVal = (val - SLIDER_SPLIT) / (SLIDER_MAX - SLIDER_SPLIT);
    const acres = ACRES_MIN + (maxAcres - ACRES_MIN) * Math.pow(normalizedVal, 2);
    return Math.round(acresToSqft(acres));
  }
}

export function lotSizeToSlider(val: number, isMax = false): number {
  const maxAcres = isMax ? ACRES_MAX_MAX : ACRES_MAX_MIN;
  const acres = sqftToAcres(val);
  
  if (acres <= ACRES_MIN) {
    // Convert to sqft scale
    return Math.round((val / SQFT_MAX) * SLIDER_SPLIT);
  } else {
    // Convert to acres scale
    const normalizedAcres = (acres - ACRES_MIN) / (maxAcres - ACRES_MIN);
    const sliderVal = SLIDER_SPLIT + Math.sqrt(normalizedAcres) * (SLIDER_MAX - SLIDER_SPLIT);
    return Math.round(sliderVal);
  }
} 